import React, { useState, useEffect } from 'react';
import { MapPin, Filter, X, ArrowRight, Building2, ChevronRight, Eye } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { 
  PageHeader, 
  Section, 
  Container, 
  SectionHeader,
  FadeInOnScroll,
  PrimaryButton
} from '../components/DesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, projects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data.projects || []);
      setFilteredProjects(response.data.projects || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/projects/categories`);
      setCategories(['All', ...(response.data.categories || [])]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const ProjectModal = ({ project, onClose }) => {
    if (!project) return null;

    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            {project.images && project.images.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 p-4">
                {project.images.map((img, idx) => (
                  <img key={idx} src={img} alt={`${project.name} ${idx + 1}`} className="w-full h-64 object-cover rounded-2xl" />
                ))}
              </div>
            ) : (
              <div className="relative h-96 overflow-hidden rounded-t-3xl">
                <img src={project.image} alt={project.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
            )}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 transition-all"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <div className="p-8 md:p-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-tight">{project.name}</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 rounded-2xl p-5">
                <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-medium">Location</p>
                <p className="font-bold text-gray-900 text-lg">{project.location}</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5">
                <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-medium">Client</p>
                <p className="font-bold text-gray-900 text-lg">{project.client}</p>
              </div>
              {project.category && (
                <div className="bg-gray-50 rounded-2xl p-5">
                  <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-medium">Category</p>
                  <p className="font-bold text-gray-900 text-lg">{project.category}</p>
                </div>
              )}
              {project.services && (
                <div className="bg-gray-50 rounded-2xl p-5">
                  <p className="text-sm text-gray-500 mb-1 uppercase tracking-wider font-medium">Services</p>
                  <p className="font-bold text-gray-900 text-lg">{project.services}</p>
                </div>
              )}
            </div>
            <div className="border-t border-gray-100 pt-8">
              <h3 className="font-bold text-gray-900 text-xl mb-4">Project Overview</h3>
              <p className="text-gray-600 leading-relaxed whitespace-pre-wrap text-lg">{project.description}</p>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scale-in {
            from { opacity: 0; transform: scale(0.95) translateY(20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          .animate-fade-in { animation: fade-in 0.3s ease-out; }
          .animate-scale-in { animation: scale-in 0.4s ease-out; }
        `}</style>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-6 text-gray-600 font-medium text-lg">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="projects-page">
      <SEO 
        title="Our Projects"
        description="Explore FURD Ltd's portfolio of MEP engineering projects across retail, commercial, residential and leisure sectors."
        keywords="MEP projects, building services portfolio, engineering case studies"
      />

      {/* Hero Section */}
      <PageHeader
        badge="Our Work"
        badgeIcon={Building2}
        title="Project Portfolio"
        subtitle="Explore our diverse portfolio of MEP engineering projects across various sectors."
      />

      {/* Filter Section */}
      <Section background="white" padding="sm">
        <Container>
          <div className="flex items-center gap-4 flex-wrap py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 text-gray-500">
              <Filter className="w-5 h-5" />
              <span className="font-semibold">Filter:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                    selectedCategory === category 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-600/20' 
                      : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                  data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Projects Grid */}
      <Section background="light" padding="default">
        <Container>
          {filteredProjects.length === 0 ? (
            <FadeInOnScroll>
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 text-xl font-medium">No projects found in this category.</p>
                <button 
                  onClick={() => setSelectedCategory('All')}
                  className="mt-6 text-blue-600 font-semibold hover:underline"
                >
                  View all projects
                </button>
              </div>
            </FadeInOnScroll>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <FadeInOnScroll key={project._id} delay={index * 100}>
                  <div
                    className="group cursor-pointer bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl border border-gray-100/80 hover:border-blue-200/50 scroll-smooth-card"
                    onClick={() => setSelectedProject(project)}
                    data-testid={`project-card-${project._id}`}
                  >
                    <div className="h-64 scroll-smooth-image">
                      <img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                      {/* Overlay with icon on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                        <div className="w-full">
                          <p className="text-white/90 text-sm line-clamp-2 mb-4">{project.description}</p>
                          <span className="inline-flex items-center gap-2 text-white font-semibold text-sm">
                            View Project <Eye className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                      {/* Category badge */}
                      {project.category && (
                        <div className="absolute top-4 left-4">
                          <span className="px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-gray-900">
                            {project.category}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-1">{project.name}</h3>
                      <div className="flex items-center gap-2 text-gray-500 mb-2">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{project.location}</span>
                      </div>
                      <p className="text-sm text-blue-600 font-semibold">Client: {project.client}</p>
                    </div>
                  </div>
                </FadeInOnScroll>
              ))}
            </div>
          )}
        </Container>
      </Section>

      {/* CTA Section */}
      <Section background="dark" padding="default">
        <Container className="text-center relative z-10">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-[150px] -left-[150px] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-3xl" />
          </div>
          
          <FadeInOnScroll>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">Have a Project in Mind?</h2>
            <p className="text-lg md:text-xl text-blue-100/80 mb-10 max-w-2xl mx-auto leading-relaxed">
              Let's discuss how our MEP engineering expertise can bring your vision to life.
            </p>
            <PrimaryButton to="/contact" size="lg" icon={ArrowRight}>
              Start Your Project
            </PrimaryButton>
          </FadeInOnScroll>
        </Container>
      </Section>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </div>
  );
};

export default Projects;
